/**
 * Зв’язок із сервером і черга змін, які не встигли полетіти.
 *
 * Якщо процес сервера впав, вікно лишається відкритим: правки живуть у
 * браузері й у localStorage, а коли /api/health знову відповідає — черга
 * відправляється в тому ж порядку.
 */

import { reads } from '../api/inflight.js';
import { create } from 'zustand';
import { toast } from './ui.js';
import { remainingOutbox, type MutMethod } from './offline.js';

export type { MutMethod } from './offline.js';
export { shouldQueue } from './offline.js';

const OUTBOX_KEY = 'kanc.outbox';
const PING_MS = 4000;
const HEALTHY_PING_MS = 30_000;
const PING_TIMEOUT = 2500;

export interface OutboxItem {
  id: string;
  method: MutMethod;
  path: string;
  body: unknown;
}

/** Зміну прийнято локально і вона чекає сервера. */
export class OfflineQueued extends Error {
  constructor() {
    super('Немає зв’язку з сервером. Зміну збережено тут і надішлеться пізніше.');
    this.name = 'OfflineQueued';
  }
}

export const isOfflineQueued = (e: unknown): e is OfflineQueued => e instanceof OfflineQueued;

function readOutbox(): OutboxItem[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as OutboxItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeOutbox(items: OutboxItem[]): void {
  try {
    if (items.length) localStorage.setItem(OUTBOX_KEY, JSON.stringify(items));
    else localStorage.removeItem(OUTBOX_KEY);
  } catch {
    /* квота або приватний режим */
  }
}

/** Що сервер розповів про себе у відповідь на /api/health. */
export interface ServerInfo {
  /** Секунд від запуску процесу. */
  uptime: number;
  /** Час на сервері за його годинником. */
  time: string;
  db: boolean;
}

interface PingResult {
  ok: boolean;
  /** Скільки мілісекунд ішла відповідь; є й у невдалого запиту. */
  ms: number;
  info: ServerInfo | null;
}

async function pingHealth(): Promise<PingResult> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), PING_TIMEOUT);
  const t0 = performance.now();
  try {
    const res = await fetch('/api/health', { cache: 'no-store', signal: ctrl.signal });
    const ms = Math.round(performance.now() - t0);
    let info: ServerInfo | null = null;
    try {
      const body = (await res.json()) as Partial<ServerInfo>;
      info = {
        uptime: typeof body.uptime === 'number' ? body.uptime : 0,
        time: typeof body.time === 'string' ? body.time : '',
        db: body.db !== false,
      };
    } catch {
      /* відповів не наш сервер, а щось перед ним — стану не знаємо */
    }
    return { ok: res.ok, ms, info };
  } catch {
    return { ok: false, ms: Math.round(performance.now() - t0), info: null };
  } finally {
    clearTimeout(timer);
  }
}

interface LinkState {
  online: boolean;
  syncing: boolean;
  /** Скільки PUT/PATCH/DELETE чекають у загальній черзі. */
  outbox: number;
  /** Скільки операцій позначок ще не на сервері. */
  marksPending: number;
  /** Час відповіді останнього опитування, мс; null — ще не питали. */
  pingMs: number | null;
  /** Коли востаннє питали сервер (Date.now()). */
  pingAt: number | null;
  /** Стан сервера з останньої вдалої відповіді. */
  server: ServerInfo | null;
  watch: () => () => void;
  ping: (flushIfUp?: boolean) => Promise<boolean>;
  enqueue: (item: Omit<OutboxItem, 'id'>) => void;
  flush: () => Promise<void>;
  setOnline: (v: boolean) => void;
  setMarksPending: (n: number) => void;
}

let healthRequest: Promise<PingResult> | null = null;

function sharedHealth(): Promise<PingResult> {
  return (healthRequest ??= pingHealth().finally(() => {
    healthRequest = null;
  }));
}

let flushing = false;
let interval: ReturnType<typeof setInterval> | null = null;

export const useLink = create<LinkState>((set, get) => ({
  online: true,
  syncing: false,
  outbox: readOutbox().length,
  marksPending: 0,
  pingMs: null,
  pingAt: null,
  server: null,

  setOnline: v => {
    const was = get().online;
    if (was === v) return;
    set({ online: v });
    if (!v) toast('Немає зв’язку з сервером. Зміни зберігаються на цьому комп’ютері');
    else toast('Зв’язок із сервером відновлено', 'ok');
  },

  setMarksPending: n => set({ marksPending: n }),

  enqueue: item => {
    const next = [
      ...readOutbox(),
      { ...item, id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}` },
    ];
    writeOutbox(next);
    set({ outbox: next.length });
    get().setOnline(false);
  },

  ping: async flushIfUp => {
    const r = await sharedHealth();
    set({ pingMs: r.ms, pingAt: Date.now(), server: r.info ?? get().server });
    get().setOnline(r.ok);
    if (r.ok && flushIfUp) await get().flush();
    return r.ok;
  },

  flush: async () => {
    if (flushing) return;
    const items = readOutbox();
    if (!items.length) return;
    flushing = true;
    reads.invalidate();
    set({ syncing: true });
    const left: OutboxItem[] = [];
    let sent = 0;
    try {
      for (const item of items) {
        try {
          const res = await fetch(item.path, {
            method: item.method,
            headers: item.body === undefined ? undefined : { 'Content-Type': 'application/json' },
            body: item.body === undefined ? undefined : JSON.stringify(item.body),
            credentials: 'same-origin',
          });
          if (res.ok || res.status === 204) {
            sent += 1;
            continue;
          }
          if (res.status === 401) {
            left.push(item, ...items.slice(items.indexOf(item) + 1));
            break;
          }
          if (res.status === 502 || res.status === 503 || res.status === 504) {
            left.push(item, ...items.slice(items.indexOf(item) + 1));
            get().setOnline(false);
            break;
          }
          /* 4xx: повтор не допоможе — відкидаємо, щоб черга не сталася. */
        } catch {
          left.push(item, ...items.slice(items.indexOf(item) + 1));
          get().setOnline(false);
          break;
        }
      }
      // Зміни, додані під час відправлення, належать наступному проходу.
      const remaining = remainingOutbox(items, left, readOutbox());
      writeOutbox(remaining);
      set({ outbox: remaining.length });
      if (sent)
        toast(
          sent === 1 ? 'Надіслано збережену зміну' : `Надіслано збережених змін: ${sent}`,
          'ok',
        );
    } finally {
      reads.invalidate();
      flushing = false;
      set({ syncing: false });
    }
  },

  watch: () => {
    if (interval) clearInterval(interval);
    const tick = (): void => {
      if (document.hidden) return;
      const state = get();
      const delay =
        state.online && !state.outbox && !state.marksPending ? HEALTHY_PING_MS : PING_MS;
      if (state.pingAt !== null && Date.now() - state.pingAt < delay) return;
      void get().ping(true);
    };
    interval = setInterval(tick, PING_MS);
    const onUp = (): void => void get().ping(true);
    const onDown = (): void => get().setOnline(false);
    // Повернулися до вкладки — питаємо сервер одразу, не чекаючи такту.
    const onVisible = (): void => {
      if (!document.hidden) void get().ping(true);
    };
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    document.addEventListener('visibilitychange', onVisible);
    void tick();
    return () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      window.removeEventListener('online', onUp);
      window.removeEventListener('offline', onDown);
      document.removeEventListener('visibilitychange', onVisible);
    };
  },
}));
